import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons'
import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Button } from 'react-bootstrap'
import { useAuth0 } from '@auth0/auth0-react'
import SidebarNav from './SidebarNav'
import FrontlandsSponsorModal from '../../../components/FrontlandsSponsorModal'
import { apiGet } from '../../../services/AuthService'

import logo from './/./../../../assets/brand/logo'
import signet from './/./../../../assets/brand/signet'
import { ReactComponent as Logo } from './/./../../../assets/brand/coreui.svg';
import frontlandsCard from './/./../../../assets/brand/frontlands-card.png'

export default function Sidebar(props) {
    const { isShow, isShowMd, accountProfile, currentAccount, baseUrl, toggleSidebarMd } = props
    const { getAccessTokenSilently } = useAuth0()
    const [isNarrow, setIsNarrow] = useState(false)
    const [showSponsorModal, setShowSponsorModal] = useState(false)
    // First/last name + zip resolved from the account's user list + primary
    // company (same source as Admin Dashboard → Account Settings). The logged-in
    // profile claims don't carry these for every account, so we fetch them.
    const [accountDetails, setAccountDetails] = useState({ firstName: '', lastName: '', zip: '' })

    // Kill switch: the Frontlands sponsor ad is temporarily disabled everywhere.
    // Set this back to true to re-enable it (the owner/environment gating below
    // then applies as before).
    const SPONSOR_ENABLED = false

    // Sponsor gating: owners only (not operators), and only on local/dev/QA/demo — never prod.
    // Operator account types mirror the convention used in Home.js / Settings.js / SidebarNav.js.
    const isOperator =
        accountProfile?.accountType === "Admin" ||
        accountProfile?.accountType === "Sender" ||
        accountProfile?.accountType === "SenderReceiver"
    const isOwner = Boolean(accountProfile?.accountType) && !isOperator
    const isNonProdEnvironment = Boolean(
        baseUrl && (baseUrl.includes("local") || baseUrl.includes("dev") || baseUrl.includes("qa") || baseUrl.includes("demo"))
    )
    const showSponsor = SPONSOR_ENABLED && isOwner && isNonProdEnvironment

    const sponsorAccountId = accountProfile?.accountId || currentAccount?.id

    // When the sponsor is visible, pull the account's user + primary-company info
    // (a_report/accountSettingsPage) so we can prefill first/last name and zip —
    // the same data the Account Settings "Account Users" tab shows. First/last
    // come from a named user of the account (preferring the one matching the
    // signed-in email; userName is a combined "First Last" string); zip comes
    // from the primary company.
    useEffect(() => {
        if (!showSponsor || !sponsorAccountId) return
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiGet(
                    'a_report/accountSettingsPage?accountId=' + sponsorAccountId,
                    getAccessTokenSilently
                )
                if (!res || !res.ok) return
                const dto = await res.json()
                const users = Array.isArray(dto?.users) ? dto.users : []
                const email = (accountProfile?.email || '').toLowerCase()
                const named = (u) => (u?.userName || '').trim().length > 0
                const emailMatch = (u) => email && (u?.userEmail || '').toLowerCase() === email
                // Prefer a user who actually has a name; use the email match only
                // to break ties (the signed-in user may have no name on this account).
                const match =
                    users.find((u) => emailMatch(u) && u.isActive && named(u)) ||
                    users.find((u) => emailMatch(u) && named(u)) ||
                    users.find((u) => u.isActive && named(u)) ||
                    users.find((u) => named(u)) ||
                    users.find((u) => emailMatch(u) && u.isActive) ||
                    users.find((u) => emailMatch(u)) ||
                    users.find((u) => u.isActive) ||
                    users[0]
                const fullName = (match?.userName || '').trim()
                const spaceIdx = fullName.indexOf(' ')
                const firstName = spaceIdx === -1 ? fullName : fullName.slice(0, spaceIdx)
                const lastName = spaceIdx === -1 ? '' : fullName.slice(spaceIdx + 1).trim()
                const zip = dto?.primaryCompanyInfo?.zip || ''
                if (cancelled) return
                setAccountDetails({ firstName, lastName, zip })
            } catch (e) {
                // ignore — the modal falls back to profile values
            }
        })()
        return () => { cancelled = true }
    }, [showSponsor, sponsorAccountId, accountProfile?.email, getAccessTokenSilently])

    // Fields shown in the sponsor modal: company + email are always available on
    // the profile; first/last/zip come from the account lookup above (falling
    // back to the profile if that hasn't resolved).
    const sponsorFirstName = accountDetails.firstName || accountProfile?.firstName || ''
    const sponsorLastName = accountDetails.lastName || accountProfile?.lastName || ''
    const sponsorFields = {
        companyName: accountProfile?.accountName || [sponsorFirstName, sponsorLastName].filter(Boolean).join(' '),
        firstName: sponsorFirstName,
        lastName: sponsorLastName,
        email: accountProfile?.email || '',
        zip: accountDetails.zip || '',
    }

    const handleSponsorClick = (e) => {
        e.preventDefault()
        setShowSponsorModal(true)
    }

    const toggleIsNarrow = () => {
        const newValue = !isNarrow
        localStorage.setItem('isNarrow', newValue ? 'true' : 'false')
        setIsNarrow(newValue)
    }

    // On first time load only
    useEffect(() => {
        if (localStorage.getItem('isNarrow')) {
            setIsNarrow(localStorage.getItem('isNarrow') === 'true')
        }
    }, [setIsNarrow])

    return (
        <div
            className={classNames('sidebar d-flex flex-column position-fixed h-100', {
                'sidebar-narrow': isNarrow,
                show: isShow,
                'md-hide': !isShowMd,
            })}
            id="sidebar"
        >
            <div className="sidebar-brand d-flex align-items-center justify-content-center" style={{ position: 'relative' }}>
                <svg
                    className="sidebar-brand-full"
                    width="118"
                    height="46"
                >
                    {`${logo}`}
                </svg>
                <Logo className="sidebar-brand-narrow d-none"
                    width="46"
                    height="46" />
                <svg
                    className="sidebar-brand-narrow d-none"
                    width="46"
                    height="46"
                >
                    {`${signet}`}
                </svg>
            </div>

            <div className="sidebar-nav flex-fill">
                <SidebarNav accountProfile={accountProfile} currentAccount={currentAccount} baseUrl={baseUrl} />
            </div>

            {showSponsor && (
                // Opens the Frontlands sponsor pop-up modal (no navigation / new tab).
                <a
                    className="sidebar-sponsor"
                    href="#"
                    role="button"
                    onClick={handleSponsorClick}
                >
                    <span className="sidebar-sponsor-label">Sponsored</span>
                    <img className="sidebar-sponsor-card" src={frontlandsCard} alt="Frontlands" />
                    <span className="sidebar-sponsor-title">Turn mineral rights into cash</span>
                    <span className="sidebar-sponsor-subtitle">Frontlands line of credit</span>
                    <span className="sidebar-sponsor-cta">See if you qualify &#8599;</span>
                    <span className="sidebar-sponsor-domain">frontlands.com</span>
                </a>
            )}

            <FrontlandsSponsorModal
                show={showSponsorModal}
                onHide={() => setShowSponsorModal(false)}
                fields={sponsorFields}
            />

            {/*<Button*/}
            {/*  variant="link"*/}
            {/*  className="sidebar-toggler d-none d-md-inline-block rounded-0 text-end pe-4 fw-bold shadow-none"*/}
            {/*  onClick={toggleIsNarrow}*/}
            {/*  type="button"*/}
            {/*  aria-label="sidebar toggler"*/}
            {/*>*/}
            {/*  <FontAwesomeIcon className="sidebar-toggler-chevron" icon={faAngleLeft} fontSize={24} />*/}
            {/*</Button>*/}

            <Button
                variant="link"
                className="sidebar-toggler d-none d-md-inline-block rounded-0 text-end pe-4 fw-bold shadow-none"
                onClick={toggleSidebarMd}
                type="button"
                aria-label="close sidebar"
            >
                <FontAwesomeIcon className="sidebar-toggler-chevron" icon={faAngleLeft} fontSize={18} />
            </Button>
        </div>
    )
}

export const SidebarOverlay = (props) => {
    const { isShowSidebar, toggleSidebar } = props

    return (
        <div
            tabIndex={-1}
            aria-hidden
            className={classNames('sidebar-overlay position-fixed top-0 bg-dark w-100 h-100 opacity-50', {
                'd-none': !isShowSidebar,
            })}
            onClick={toggleSidebar}
        />
    )
}
